import { METERS_TO_FT } from '../classes/math.utilities';
import { BuildPriority } from './pitches.enums';
import { TrackingDevice } from './training.enums';

export const MIN_BREAK_CONFIDENCE = 1;
export const MIN_BREAK_THRESHOLD_FT = 2.25 / METERS_TO_FT;
export const MAX_BREAK_THRESHOLD_FT = 3 / METERS_TO_FT;

// kinds of things we can filter by
export enum ShotCriteria {
  ExistsBallState = 'Exists: Ball State',

  ConfidenceSpin = 'Confidence: Spin', // * should be ignored for Vision + Trackman
  ConfidenceBreak = 'Confidence: Break', // trajectory_break_confidence
  ConfidencePlates = 'Confidence: Plates',
  ConfidenceSpeed = 'Confidence: Speed',

  ModelGyroSpin = 'Model: Gyro Spin', // * should be ignored for Vision + Trackman, aka: gyro_spin_diff
  ModelNonGyroSpin = 'Model: Non-Gyro Spin', // * should be ignored for Vision + Trackman, aka: non_gyro_spin_diff
  ModelZeroTrajBreak = 'Model: Zero Traj Break', // zero_trajectory_break

  RapsodoBreakAgreement = 'Rapsodo: Break Agreement', // break_diff
}

export const ALL_SHOT_CRITERIA = Object.values(ShotCriteria) as ShotCriteria[];

export const getConfidenceCriteria = (priority: BuildPriority | undefined) => {
  switch (priority) {
    case BuildPriority.Breaks: {
      return [
        ShotCriteria.ConfidencePlates,
        ShotCriteria.ConfidenceBreak,
        ShotCriteria.ConfidenceSpeed,
      ];
    }

    case BuildPriority.Default:
    case BuildPriority.Spins:
    default: {
      return [
        ShotCriteria.ExistsBallState,
        ShotCriteria.ConfidencePlates,
        ShotCriteria.ConfidenceSpin,
        ShotCriteria.ConfidenceSpeed,
      ];
    }
  }
};

export const getAllCriteria = (priority: BuildPriority | undefined) => {
  return [
    ...getConfidenceCriteria(priority),
    ShotCriteria.ModelGyroSpin,
    ShotCriteria.ModelNonGyroSpin,
    ShotCriteria.ModelZeroTrajBreak,
    ShotCriteria.RapsodoBreakAgreement,
  ];
};

export const getTrackingDeviceCriteria = (trackingDevice: TrackingDevice) => {
  switch (trackingDevice) {
    case TrackingDevice.RapsodoV3PRO: {
      return [
        ShotCriteria.ConfidencePlates,
        ShotCriteria.ConfidenceSpin,
        ShotCriteria.ConfidenceSpeed,
        ShotCriteria.ConfidenceBreak,
      ];
    }

    case TrackingDevice.TrackmanB1: // no spin confidence w/o release extension
    case TrackingDevice.TrajektVision: // no spin confidence
    default: {
      return [
        ShotCriteria.ConfidencePlates,
        ShotCriteria.ConfidenceSpeed,
        ShotCriteria.ConfidenceBreak,
      ];
    }
  }
};

// just a clone of ALL_SHOT_CRITERIA
export const MODELLING_SHOT_CRITERIA = ALL_SHOT_CRITERIA.map((c) => c);

// * nothing is fatal for now
export const FATAL_MODELLING_CRITERIA: ShotCriteria[] = [
  // ShotCriteria.ModelGyroSpin,
  // ShotCriteria.ModelNonGyroSpin,
];

// kinds of things we can filter by
export enum GroupCriteria {
  Speed = 'Speed',
  Breaks = 'Breaks',
  Spins = 'Spins',
  NewestInZone = 'Newest In Zone',
}

export const getGroupCriteria = (priority: BuildPriority | undefined) => {
  switch (priority) {
    case BuildPriority.Breaks: {
      return [
        GroupCriteria.Speed,
        GroupCriteria.Breaks,
        GroupCriteria.NewestInZone,
      ];
    }

    case BuildPriority.Default:
    case BuildPriority.Spins:
    default: {
      return [
        GroupCriteria.Speed,
        GroupCriteria.Spins,
        GroupCriteria.NewestInZone,
      ];
    }
  }
};

// kinds of reasons for a shot group to be rejected
export enum GroupRejectedReason {
  TooFew = 'Too Few',
  NoMeanBs = 'No Mean BS',
  NoMeanTraj = 'No Mean Traj',
  NoMeanBreaks = 'No Mean Breaks',
  NoNewestShot = 'No Newest Shot',
  NoTargetBreaks = 'No Target Breaks',
  Speed = 'Speed',
  BreakX = 'Break X',
  BreakZ = 'Break Z',
  SpinX = 'Spin X',
  SpinY = 'Spin Y',
  SpinZ = 'Spin Z',
  NewestInZone = 'Newest In Zone',
}
